import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  PremioPublic,
  RaspaditaGridDinamica,
  SoundStyle,
} from "@juegossc/shared";
import { playEvent } from "../../lib/audio";

/** Resultado pasado al callback onRevealed para que el caller decida
 *  qué modal mostrar (premio real o consolation). El server sigue siendo
 *  authoritative del premio elegido, pero en grid el match efectivo del
 *  cliente determina si "ganó" desde su perspectiva. */
export type GridRevealResult = {
  /** True si el cliente raspó 3 iguales del premio elegido (target
   *  real). False si no llegó a matchear o si el target era consolation. */
  matchedWin: boolean;
};

type Props = {
  width: number;
  height: number;
  /** Premio elegido por el server. Se ubican 3 casillas con este símbolo. */
  target: PremioPublic;
  /** Otros premios disponibles para usar como distractores. */
  distractores: PremioPublic[];
  /** Llamado cuando el cliente termina de raspar (según la dinámica). */
  onRevealed?: (result: GridRevealResult) => void;
  /** Dinámica configurada por admin:
   *  - encontrar: sin límite, auto-reveal al detectar 3 iguales del target
   *  - intentos: solo 3 raspados; al 3ro auto-reveal de las 9
   *  - todas: cliente raspa las 9; al terminar la última, reveal
   *  Default: encontrar (comportamiento original).
   */
  dinamica?: RaspaditaGridDinamica;
  /** Estilo de sonido — se pasa a cada celda para los efectos de raspado
   *  y revelación (refactor sonidos 2026-06-02). */
  soundStyle?: SoundStyle;
  /** Migration 040: cap de DPR de cada celda-canvas (nitidez). Default 2. */
  dprMax?: number;
};

const SCRATCH_THROTTLE_MS = 70;

const GRID = 3; // 3x3 = 9 casillas
const TARGET_COUNT = 3; // siempre 3 del target en el grid

// Fisher-Yates shuffle in-place
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Cuántas veces puede repetirse un distractor que NO es consolation.
// Limitamos a 2 para que el cliente NUNCA vea 3 iguales de un premio
// "bueno" cuando el target real es otro — eso confundía: el cliente
// pensaba que ganó el distractor y se quedaba esperando que aparezca
// algo. Las consolations (Siga participando, etc.) no tienen límite
// porque ver 3 iguales de consolation no engaña a nadie.
const MAX_REAL_DISTRACTOR_REPEAT = 2;

function generateSymbols(
  target: PremioPublic,
  distractores: PremioPublic[],
): PremioPublic[] {
  const total = GRID * GRID;
  const arr = new Array<PremioPublic | undefined>(total).fill(undefined);

  // Caso edge: sin distractores → todas target.
  if (distractores.length === 0) {
    return new Array(total).fill(target);
  }

  // Posiciones random para las 3 del target (estas 3 garantizan que
  // SIEMPRE hay al menos 3 con el id del target en el grid → el
  // cliente puede "encontrar 3 iguales" del premio elegido).
  const positions = shuffle([...Array(total).keys()]);
  positions.slice(0, TARGET_COUNT).forEach((p) => (arr[p] = target));

  const fillerSlots = total - TARGET_COUNT; // 6

  // Estrategia del filler — pensada para dar VARIEDAD visual:
  //  1) Primer pase: una copia de CADA distractor distinto (mezclado
  //     consolation + reales). Máxima diversidad — el cliente ve
  //     muchos premios distintos en el grid → más ilusión, no se sabe
  //     desde el principio "esto es target y filler todos iguales".
  //  2) Si quedan huecos (poca variedad de distractores), repetir
  //     respetando caps:
  //       - distractores reales: max 2 cada uno (para no causar falsa
  //         señal de "encontré 3 iguales" de un premio que no era el
  //         target).
  //       - consolations: sin cap (3 sigas no engañan a nadie).
  //  3) Último recurso si nada calza: target (no afecta el resultado
  //     porque el match cuenta por id y el cliente igual ve 3+ del
  //     premio que se va a llevar).
  const shuffledDistractores = shuffle([...distractores]);
  const filler: PremioPublic[] = [];

  // Primer pase: variedad — uno de cada distractor distinto.
  for (const d of shuffledDistractores) {
    if (filler.length >= fillerSlots) break;
    filler.push(d);
  }

  // Segundo pase: completar huecos respetando caps.
  if (filler.length < fillerSlots) {
    const realCount = new Map<number, number>();
    for (const f of filler) {
      if (!f.is_consolation) {
        realCount.set(f.id, (realCount.get(f.id) || 0) + 1);
      }
    }
    const consolationDistractors = distractores.filter((p) => p.is_consolation);
    const realDistractors = distractores.filter((p) => !p.is_consolation);

    while (filler.length < fillerSlots) {
      const candidates: PremioPublic[] = [
        ...consolationDistractors,
        ...realDistractors.filter(
          (r) => (realCount.get(r.id) || 0) < MAX_REAL_DISTRACTOR_REPEAT,
        ),
      ];
      if (candidates.length === 0) {
        filler.push(target); // último recurso
      } else {
        const pick = candidates[Math.floor(Math.random() * candidates.length)];
        filler.push(pick);
        if (!pick.is_consolation) {
          realCount.set(pick.id, (realCount.get(pick.id) || 0) + 1);
        }
      }
    }
  }

  const shuffledFiller = shuffle(filler);
  let f = 0;
  for (let i = 0; i < total; i++) {
    if (arr[i] === undefined) {
      arr[i] = shuffledFiller[f++];
    }
  }
  return arr as PremioPublic[];
}

const TOTAL_CASILLAS = GRID * GRID; // 9
const INTENTOS_MAX = 3;

export function ScratchGrid({
  width,
  height,
  target,
  distractores,
  onRevealed,
  dinamica = "encontrar",
  soundStyle = "classic",
  dprMax = 2,
}: Props) {
  // Generar símbolos UNA SOLA VEZ por mount.
  // useState lazy corre exactamente 1 vez; el padre pasa key={prize.id}
  // para forzar remount cuando cambia la jugada.
  const [symbols] = useState<PremioPublic[]>(() =>
    generateSymbols(target, distractores),
  );

  const [revealedSet, setRevealedSet] = useState<Set<number>>(new Set());
  // Casillas que se resaltan con parpadeo cuando se gana — son las 3
  // del match. Vacío cuando no se ganó (caso consolation o imposible).
  // El parpadeo le muestra al cliente CUÁLES fueron las 3 que matchearon
  // antes de que aparezca el modal (mismo patrón que el segmento ganador
  // de la ruleta).
  const [highlightedSet, setHighlightedSet] = useState<Set<number>>(new Set());
  // Overlay rojo "casi" sobre la grilla durante el delay antes de abrir
  // el modal. Mismo patrón que el overlay de memoria-perdiste — el
  // cliente necesita un beat visual para procesar "no me salió". Sin
  // esto el modal salía abrupto justo después del último raspado.
  const [showLostOverlay, setShowLostOverlay] = useState(false);
  const allRevealedRef = useRef(false);
  // Timer del reveal en ref propio — NO en el cleanup del useEffect.
  // Si estuviera en el cleanup, el re-render que dispara setRevealedSet
  // adentro del trigger lo mataría antes de que onRevealed se ejecute.
  const revealTimerRef = useRef<number | null>(null);

  // Cálculo del tamaño que respeta padding del container (p-3 = 12px)
  // y gap entre celdas (gap-2 = 8px). Antes era `Math.min/3 - 6` que
  // no encajaba con esos valores → el rectángulo de fondo quedaba más
  // grande que la suma de las celdas y se veía desalineado.
  const GRID_PADDING = 12;
  const GRID_GAP = 8;
  // Celdas RECTANGULARES: ancho desde width, alto desde height (cada uno
  // independiente). Antes era cuadrado min(width,height) -> el slider de ancho
  // no hacía efecto pasado el alto (y viceversa). Ahora el grid llena el
  // rectángulo W×H, así "Ancho del panel" ensancha y "Alto del panel" agranda,
  // como los labels prometen.
  const availW = width - 2 * GRID_PADDING - (GRID - 1) * GRID_GAP;
  const availH = height - 2 * GRID_PADDING - (GRID - 1) * GRID_GAP;
  const cellW = Math.floor(availW / GRID);
  const cellH = Math.floor(availH / GRID);
  // Container exacto: 3 celdas + 2 gaps + 2 padding por eje. Garantiza que el
  // bg-[#1a1540] del contenedor encaje exacto con las casillas (sin sobrante).
  const containerW = cellW * GRID + (GRID - 1) * GRID_GAP + 2 * GRID_PADDING;
  const containerH = cellH * GRID + (GRID - 1) * GRID_GAP + 2 * GRID_PADDING;
  const raspadasCount = revealedSet.size;
  const raspadasRestantes =
    dinamica === "intentos" ? Math.max(0, INTENTOS_MAX - raspadasCount) : null;

  function handleCellReveal(idx: number) {
    if (allRevealedRef.current) return;
    setRevealedSet((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  }

  // Trigger del reveal — observamos revealedSet con useEffect.
  //
  // En "encontrar" el match cuenta CUALQUIER 3 casillas reveladas con
  // el mismo id que el target. Esto es lo natural para el cliente:
  // si visualmente ve 3 iguales, espera que dispare el modal.
  // generateSymbols garantiza que al menos 3 casillas tengan el id
  // del target — el cliente siempre puede ganar/perder según lo
  // que el server eligió (server-authoritative).
  //
  // OJO: NO devolver cleanup con clearTimeout. Cuando disparamos
  // setRevealedSet adentro del trigger, React re-corre el effect y
  // llamaría el cleanup del run anterior → mataría el setTimeout
  // antes de que onRevealed se ejecute. El cleanup del timer vive
  // en otro useEffect dedicado al unmount (más abajo).
  useEffect(() => {
    if (allRevealedRef.current) return;

    // Triple decisión: ¿disparar?, ¿es win? (para parpadeo), ¿revelar el resto?
    let shouldReveal = false;
    let isWin = false; // true cuando el cliente ganó un premio real
    const targetRevealed = Array.from(revealedSet).filter(
      (i) => symbols[i].id === target.id,
    ).length;

    // "todas" (legacy) se trata como "encontrar". El admin no puede
    // elegir "todas" en la UI nueva — solo queda como valor válido en
    // DB para back-compat con instalaciones viejas.
    if (dinamica === "encontrar" || dinamica === "todas") {
      const sinRaspar = TOTAL_CASILLAS - revealedSet.size;
      const faltan = TARGET_COUNT - targetRevealed;
      if (!target.is_consolation) {
        if (targetRevealed >= TARGET_COUNT) {
          shouldReveal = true;
          isWin = true; // matcheó 3 del premio real
        } else if (sinRaspar < faltan) {
          shouldReveal = true; // imposible matchear → cerrar (no es win)
        }
      } else {
        if (sinRaspar <= 2) {
          shouldReveal = true; // target consolation y quedan ≤ 2 → cerrar
        }
      }
    } else if (dinamica === "intentos") {
      if (revealedSet.size >= INTENTOS_MAX) {
        shouldReveal = true;
        // En "intentos" también es win si entre las 3 raspadas
        // matcheó 3 del target real.
        if (!target.is_consolation && targetRevealed >= TARGET_COUNT) {
          isWin = true;
        }
      }
    }

    if (!shouldReveal) return;
    allRevealedRef.current = true;

    // Destapar el resto solo en "intentos" Y solo si NO ganó.
    // Si el cliente raspó sus 3 intentos y matcheó 3 del target (ganó),
    // dejamos solo las 3 ganadoras parpadeando y modal — igual que en
    // encontrar. Si NO ganó, sí mostramos las 9 (es la sorpresa de la
    // mecánica intentos).
    if (dinamica === "intentos" && !isWin) {
      setRevealedSet((prev) => {
        const next = new Set(prev);
        for (let i = 0; i < symbols.length; i++) next.add(i);
        return next;
      });
    }

    // Si es win, resaltar las 3 casillas del match con parpadeo y dar
    // tiempo al cliente para verlas ANTES del modal. Mismo patrón que
    // el segmento ganador de la ruleta — sirve para que entienda
    // visualmente "ahí están las 3 iguales que matcheaste".
    //
    // Si NO es win, overlay rojo sobre la grilla durante 2s antes del
    // modal "Casi!" — mismo respiro que en memoria-perdiste. Sin esto
    // el modal saltaba en 600ms y el cliente no entendía qué pasó.
    let delay;
    if (isWin) {
      const matching = Array.from(revealedSet)
        .filter((i) => symbols[i].id === target.id)
        .slice(0, TARGET_COUNT);
      setHighlightedSet(new Set(matching));
      delay = 1800; // 3 parpadeos de ~500ms + un toque extra
    } else {
      setShowLostOverlay(true);
      delay = 2000;
    }

    if (revealTimerRef.current !== null) {
      window.clearTimeout(revealTimerRef.current);
    }
    // Capturamos isWin en una const local para que el callback la use
    // (closure). Pasamos al caller para que decida ganaste vs consolation
    // — server-authoritative del premio elegido, pero el matchee efectivo
    // del cliente determina si "ganó" visualmente.
    const matchedWin = isWin;
    revealTimerRef.current = window.setTimeout(() => {
      revealTimerRef.current = null;
      onRevealed?.({ matchedWin });
    }, delay);
  }, [revealedSet, dinamica, symbols, target.id, target.is_consolation, onRevealed]);

  // Cleanup del timer al desmontar el componente. SEPARADO del effect
  // del trigger para que solo corra al unmount real (no en cada
  // re-render del trigger). Esto evita que un setRevealedSet del
  // trigger mate su propio setTimeout.
  useEffect(() => {
    return () => {
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative grid gap-2 rounded-2xl bg-[#1a1540] p-3 shadow-2xl"
        style={{
          gridTemplateColumns: `repeat(${GRID}, ${cellW}px)`,
          gridTemplateRows: `repeat(${GRID}, ${cellH}px)`,
          width: containerW,
          height: containerH,
          // touch-action none + overscroll contain en el CONTENEDOR (no solo
          // en cada canvas): cubre los gaps/padding entre celdas, donde un
          // dedo vertical scrolleaba/recargaba la pagina.
          touchAction: "none",
          overscrollBehavior: "contain",
        }}
      >
        {symbols.map((sym, idx) => (
          <ScratchCell
            key={idx}
            cellW={cellW}
            cellH={cellH}
            symbol={sym}
            revealed={revealedSet.has(idx)}
            // Resaltada con parpadeo cuando es una de las 3 del match
            // (después de ganar). Vacío en perder/consolation.
            highlighted={highlightedSet.has(idx)}
            // En modo 'intentos', deshabilitar el resto cuando ya raspó 3.
            disabled={
              dinamica === "intentos" &&
              raspadasCount >= INTENTOS_MAX &&
              !revealedSet.has(idx)
            }
            soundStyle={soundStyle}
            dprMax={dprMax}
            onScratched={() => handleCellReveal(idx)}
          />
        ))}
        {/* Overlay rojo del "casi" — animate-[memoria-lost-fade] fades
            in y se queda hasta que el modal cubre todo. pointer-events
            none para no bloquear si el cliente intenta raspar más
            durante el respiro (es no-op igual porque allRevealedRef
            ya está en true). z-20 va encima de las casillas (z-10) pero
            debajo del modal (z-50). */}
        {showLostOverlay && (
          <div
            className="pointer-events-none absolute inset-0 z-20 animate-[memoria-lost-fade_2s_ease-out_forwards] rounded-2xl bg-red-600"
            aria-hidden="true"
          />
        )}
      </div>
      {/* Contador de raspados restantes solo en modo 'intentos' */}
      {dinamica === "intentos" && !allRevealedRef.current && (
        <div className="text-sm opacity-70">
          Te quedan <span className="font-bold">{raspadasRestantes}</span>{" "}
          {raspadasRestantes === 1 ? "raspada" : "raspadas"}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// ScratchCell — mini canvas raspable individual
// ──────────────────────────────────────────────
type CellProps = {
  // Celda rectangular (W×H). Antes era un único `size` cuadrado.
  cellW: number;
  cellH: number;
  symbol: PremioPublic;
  revealed: boolean;
  /** Si true, el canvas ignora eventos (modo 'intentos' tras llegar al límite). */
  disabled?: boolean;
  /** Si true, esta celda forma parte del match ganador → parpadea con
   *  ring dorado para resaltarla antes de que aparezca el modal. */
  highlighted?: boolean;
  /** Estilo de sonido — se usa para scratch + reveal de la celda. */
  soundStyle: SoundStyle;
  /** Migration 040: cap de DPR del canvas de la celda (nitidez). */
  dprMax: number;
  onScratched: () => void;
};

const SCRATCH_RADIUS = 18;
const REVEAL_THRESHOLD = 0.4; // 40% raspado dispara reveal
// Idle timer por celda — mismo patrón que ScratchPanel. Si el cliente
// raspa un toque y se queda quieto 1.2s, con que tenga >=10% raspado
// ya se revela. Sin esto el cliente raspaba apenas (suficiente para
// "ver" el símbolo) y la celda nunca se revelaba.
// Celdas son más chicas (~80×80) que el panel grande, así que 10% va
// más rápido pero igual requiere un toque deliberado (no se dispara
// por un click suelto).
const CELL_IDLE_MS = 1200;
const CELL_IDLE_MIN_PCT = 0.1;

function ScratchCell({ cellW, cellH, symbol, revealed, disabled, highlighted, soundStyle, dprMax, onScratched }: CellProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const internalRevealed = useRef(false);
  const idleTimerRef = useRef<number | null>(null);
  const lastScratchSoundRef = useRef(0);

  function maybePlayScratchSound() {
    const now = performance.now();
    if (now - lastScratchSoundRef.current < SCRATCH_THROTTLE_MS) return;
    lastScratchSoundRef.current = now;
    playEvent("raspadita", "scratch", soundStyle);
  }

  // Pintar overlay dorado inicial.
  //
  // useLayoutEffect — garantiza que el overlay opaco se pinte ANTES
  // del primer paint visible. Si usáramos useEffect, habría un frame
  // (~16ms) entre el paint inicial (canvas transparente) y el effect
  // (canvas pintado) donde el symbol del fondo podría ser visible y
  // el cliente vería qué hay en cada casilla sin raspar — bug que
  // rompe la mecánica del juego.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Clamp del DPR: baja el costo del getImageData por celda (x4-x9 con
    // dpr 2-3) y de los fills de raspado en kiosko. Migration 040: el cap es
    // configurable (dprMax, default 2).
    const dpr = Math.min(window.devicePixelRatio || 1, dprMax);
    canvas.width = cellW * dpr;
    canvas.height = cellH * dpr;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const gradient = ctx.createLinearGradient(0, 0, cellW, cellH);
    gradient.addColorStop(0, "#a07700");
    gradient.addColorStop(0.5, "#c89a00");
    gradient.addColorStop(1, "#a07700");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, cellW, cellH);

    // "?" centrado. Tamaño según el lado más corto para no desbordar.
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.font = `bold ${Math.min(cellW, cellH) * 0.5}px "Nunito", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("?", cellW / 2, cellH / 2);
  }, [cellW, cellH, dprMax]);

  // Si el padre marca revealed (porque ganó), limpiar overlay.
  // POR QUÉ useEffect y no useLayoutEffect: cuando revealed=true, el
  // render pone display:'none' inline en el contenedor (línea ~533),
  // así que el canvas YA no es visible cuando el clearRect corre — no
  // hay frame donde el symbol se vea sin overlay. Si en el futuro
  // alguien quita el display:'none' y mantiene el canvas visible (ej.
  // animación fade-out), este effect HAY QUE pasarlo a useLayoutEffect
  // o cambiar la estrategia (pintar transparente síncrono), sino se
  // reabre el bug "premio visible antes de raspar".
  useEffect(() => {
    if (!revealed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [revealed]);

  function getCanvasPoint(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * cellW,
      y: ((clientY - rect.top) / rect.height) * cellH,
    };
  }

  function scratchAt(x: number, y: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, SCRATCH_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    maybePlayScratchSound();
    // NO chequear reveal acá. El check solo dispara cuando soltás
    // (mouseUp/touchEnd), salís de la celda (mouseLeave) o por idle.
  }

  // Mide % raspado y revela si supera el threshold (o si force=true
  // por idle y el cliente ya raspó al menos CELL_IDLE_MIN_PCT).
  // Usamos alpha < 128 (no === 0) para capturar también los pixels
  // de borde del arc con anti-aliasing — sin esto el cálculo daba
  // siempre ~0% aunque la celda estuviera visualmente raspada.
  function checkRevealed(force = false) {
    if (internalRevealed.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let cleared = 0;
    let total = 0;
    const step = 8 * 4; // cada 8 pixels físicos, en bytes
    for (let i = 3; i < data.length; i += step) {
      total++;
      if (data[i] < 128) cleared++;
    }
    const pct = total > 0 ? cleared / total : 0;
    const shouldReveal =
      pct >= REVEAL_THRESHOLD || (force && pct >= CELL_IDLE_MIN_PCT);
    if (shouldReveal) {
      internalRevealed.current = true;
      // Refactor sonidos 2026-06-02: la celda dispara reveal cuando se
      // despeja lo suficiente. En grid hay 9 celdas independientes, así
      // que este suena varias veces por jugada (una por celda revelada).
      playEvent("raspadita", "reveal", soundStyle);
      onScratched();
    }
  }

  // Reinicia el timer del idle en cada raspado. Si no hay más eventos
  // en CELL_IDLE_MS, llama a checkRevealed(force=true) — esto permite
  // que el cliente raspe apenas (suficiente para "ver" qué hay debajo)
  // y deje de hacerlo, y la celda igual se revela sola.
  function resetIdleTimer() {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = window.setTimeout(() => {
      checkRevealed(true);
    }, CELL_IDLE_MS);
  }

  // Cleanup del timer al unmount o cuando la celda se revela —
  // evita callbacks "huérfanos" tras desmontaje.
  useEffect(() => {
    return () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  const textColor = isLightColor(symbol.color ?? "#a78bfa")
    ? "#1a1540"
    : "#ffffff";

  return (
    <div
      // animate-win-pulse: keyframe custom definido en index.css que
      // hace 3 ciclos de scale + ring dorado para resaltar las 3
      // casillas del match ganador antes del modal. Mismo patrón visual
      // que el segmento ganador parpadeante de la ruleta.
      className={`relative overflow-hidden rounded-lg ${
        highlighted ? "animate-win-pulse" : ""
      }`}
      style={{
        width: cellW,
        height: cellH,
        background: symbol.color ?? "#a78bfa",
      }}
    >
      {/* Símbolo debajo (texto del premio) — queda oculto hasta raspar */}
      <div
        className="absolute inset-0 z-0 flex items-center justify-center p-1 text-center text-[10px] font-bold leading-tight"
        style={{ color: textColor }}
      >
        {symbol.nombre}
      </div>

      {/* Canvas overlay raspable ENCIMA. pointerEvents:none cuando
          disabled (modo 'intentos' tras agotar los 3 raspados). */}
      <canvas
        ref={canvasRef}
        style={{
          width: cellW,
          height: cellH,
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 10,
          touchAction: "none",
          cursor: revealed ? "default" : disabled ? "not-allowed" : "crosshair",
          display: revealed ? "none" : "block",
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        onMouseDown={(e) => {
          if (disabled) return;
          isDrawing.current = true;
          const p = getCanvasPoint(e.clientX, e.clientY);
          if (p) scratchAt(p.x, p.y);
          resetIdleTimer();
        }}
        onMouseMove={(e) => {
          if (!isDrawing.current) return;
          const p = getCanvasPoint(e.clientX, e.clientY);
          if (p) scratchAt(p.x, p.y);
          resetIdleTimer();
        }}
        onMouseUp={() => {
          isDrawing.current = false;
          checkRevealed();
          resetIdleTimer();
        }}
        onMouseLeave={() => {
          isDrawing.current = false;
          // Mouse arrastrado fuera de la celda: chequear ya, no esperar
          // al idle. Si el cliente raspó suficiente, dispara reveal.
          checkRevealed(true);
          resetIdleTimer();
        }}
        onTouchStart={(e) => {
          isDrawing.current = true;
          const t = e.touches[0];
          if (!t) return;
          const p = getCanvasPoint(t.clientX, t.clientY);
          if (p) scratchAt(p.x, p.y);
          resetIdleTimer();
        }}
        onTouchMove={(e) => {
          if (!isDrawing.current) return;
          const t = e.touches[0];
          if (!t) return;
          const p = getCanvasPoint(t.clientX, t.clientY);
          if (p) scratchAt(p.x, p.y);
          resetIdleTimer();
        }}
        onTouchEnd={() => {
          isDrawing.current = false;
          checkRevealed();
          resetIdleTimer();
        }}
      />
    </div>
  );
}

function isLightColor(hex: string): boolean {
  const m = hex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}
